import type { APIErrorBody } from "./types";
import { analyticsActionForRequest, track } from "./analytics";

export class APIError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
    readonly suggestions: string[] = [],
  ) {
    super(message);
    this.name = "APIError";
  }
}

export async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const method = options.method ?? "GET";
  const analyticsAction = analyticsActionForRequest(path, method);
  const headers = new Headers(options.headers);
  headers.set("Accept", "application/json");
  if (options.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");

  let response: Response;
  try {
    response = await fetch(path, { ...options, headers, credentials: "include" });
  } catch {
    throw new APIError("My K-List is unreachable. Check your connection and try again.", 0, "network_error");
  }

  if (response.status === 204) {
    if (analyticsAction) track(analyticsAction.event, analyticsAction.properties);
    return undefined as T;
  }
  const body = (await response.json().catch(() => ({}))) as APIErrorBody & T;
  if (!response.ok) {
    if (analyticsAction) {
      track("product_action_failed", {
        action: analyticsAction.event,
        status: response.status,
      });
    }
    throw new APIError(
      body.error?.message ?? "The request could not be completed.",
      response.status,
      body.error?.code ?? "request_failed",
      body.error?.suggestions,
    );
  }
  if (analyticsAction) track(analyticsAction.event, analyticsAction.properties);
  return body;
}

export const imageURL = (path: string | null, size: "poster" | "backdrop" = "poster") => {
  if (!path) return null;
  return `https://image.tmdb.org/t/p/${size === "poster" ? "w500" : "original"}${path}`;
};

export const year = (date: string | null) => date?.slice(0, 4) ?? "";
